import { getEditIconSvg } from '../../icons/EditIcon';
import {
  allDays,
  defaultAveragePower,
  defaultPeakPower,
  defaultWorkingDays,
  defaultWorkingHoursEnd,
  defaultWorkingHoursStart,
  nighttimePowerRatio,
  timeIntervalMinutes,
} from '../constants';
import { PowerProfileDataPoint } from '../types';

/**
 * Parses time string (HH:MM) to hour and minute numbers
 * @param timeString - Time in HH:MM format
 * @returns Object with hour and minute numbers
 */
const parseTime = (timeString: string): { hour: number; minute: number } => {
  const [hourStr, minuteStr] = timeString.split(':');
  return {
    hour: parseInt(hourStr),
    minute: parseInt(minuteStr),
  };
};

/**
 * Calculates power value for a given time slot
 * @param hour - Current hour (0-23)
 * @param minute - Current minute (0-59)
 * @param isWorkingDay - Whether it's a working day
 * @param startHour - Working hours start hour
 * @param startMinute - Working hours start minute
 * @param endHour - Working hours end hour
 * @param endMinute - Working hours end minute
 * @param peakPowerKw - Peak power value
 * @param averagePowerKw - Average power value
 * @returns Power value in kW
 */
const calculatePowerValue = (
  hour: number,
  minute: number,
  isWorkingDay: boolean,
  startHour: number,
  startMinute: number,
  endHour: number,
  endMinute: number,
  peakPowerKw: number,
  averagePowerKw: number
): number => {
  // Calculate nighttime power (used for both non-working hours and weekends)
  const nighttimePower = averagePowerKw * nighttimePowerRatio;

  // Determine power state based on day type and time
  const getPowerState = (): 'weekend' | 'nighttime' | 'working-hours' => {
    if (!isWorkingDay) {
      return 'weekend';
    }

    const currentTimeMinutes = hour * 60 + minute;
    const startTimeMinutes = startHour * 60 + startMinute;
    const endTimeMinutes = endHour * 60 + endMinute;

    if (currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < endTimeMinutes) {
      return 'working-hours';
    }

    return 'nighttime';
  };

  const powerState = getPowerState();

  switch (powerState) {
    case 'weekend':
    case 'nighttime':
      return nighttimePower;

    case 'working-hours': {
      const currentTimeMinutes = hour * 60 + minute;
      const startTimeMinutes = startHour * 60 + startMinute;
      const endTimeMinutes = endHour * 60 + endMinute;

      // Calculate the exact middle of working hours
      const workingDurationMinutes = endTimeMinutes - startTimeMinutes;
      const middleTimeMinutes = startTimeMinutes + workingDurationMinutes / 2;

      // Peak power for 1 hour (60 minutes) centered on the middle of working hours
      const peakStartMinutes = middleTimeMinutes - 30; // 30 minutes before middle
      const peakEndMinutes = middleTimeMinutes + 30; // 30 minutes after middle

      if (currentTimeMinutes >= peakStartMinutes && currentTimeMinutes < peakEndMinutes) {
        return peakPowerKw;
      } else {
        return averagePowerKw;
      }
    }

    default:
      return nighttimePower;
  }
};

/**
 * Generates power profile data for one week based on configuration
 * @param peakPowerKw - Peak power in kW
 * @param averagePowerKw - Average power in kW
 * @param workingDays - Array of working days
 * @param workingHoursStart - Working hours start time (HH:MM format)
 * @param workingHoursEnd - Working hours end time (HH:MM format)
 * @returns Array of power profile data points
 */
export const generatePowerProfileData = (
  peakPowerKw: number = defaultPeakPower,
  averagePowerKw: number = defaultAveragePower,
  workingDays: string[] = defaultWorkingDays,
  workingHoursStart: string = defaultWorkingHoursStart,
  workingHoursEnd: string = defaultWorkingHoursEnd
): PowerProfileDataPoint[] => {
  // Parse working hours
  const startTime = parseTime(workingHoursStart);
  const endTime = parseTime(workingHoursEnd);

  const generatedData: PowerProfileDataPoint[] = [];

  // Get Monday of current week
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday is 1, Sunday is 0
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);

  // Generate data for one week (7 days)
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayName = allDays[dayIndex];
    const isWorkingDay = workingDays.includes(dayName);
    const currentDay = new Date(monday);
    currentDay.setDate(monday.getDate() + dayIndex);

    // Generate data every 15 minutes throughout the day
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += timeIntervalMinutes) {
        const date = new Date(currentDay);
        date.setHours(hour, minute, 0, 0);

        const powerValue = calculatePowerValue(
          hour,
          minute,
          isWorkingDay,
          startTime.hour,
          startTime.minute,
          endTime.hour,
          endTime.minute,
          peakPowerKw,
          averagePowerKw
        );

        generatedData.push({
          timestamp: date.toISOString(),
          power: Math.round(powerValue * 10) / 10, // Round to 1 decimal
          day: dayName,
          hour: hour,
          dayIndex: dayIndex,
        });
      }
    }
  }

  return generatedData;
};

/**
 * Calculates chart color based on provided color or theme primary color
 *
 * @param color - Optional custom color
 * @param themePrimaryColor - Primary color from theme
 * @returns Chart color string
 */
export const calculateChartColor = (color: string | undefined, themePrimaryColor: string): string => {
  return color || themePrimaryColor;
};

/**
 * Creates a custom cursor CSS value from Edit icon SVG
 * The cursor appears as a small edit pencil icon
 *
 * @param color - Optional color for the icon (defaults to black)
 * @returns CSS cursor string with data URL
 */
export const createEditCursor = (color: string = '#000000'): string => {
  // Normalize color - convert rgb() to hex if needed
  let normalizedColor = color;
  if (color.startsWith('rgb')) {
    // For simplicity, just use black for rgb colors
    normalizedColor = '#000000';
  }

  // Get Edit icon SVG from ui-library icons (reused from EditIcon component)
  const svg = getEditIconSvg(normalizedColor, 2, 20, 20);

  // Encode SVG to data URL with proper escaping
  const encodedSvg = svg
    .replace(/"/g, "'")
    .replace(/%/g, '%25')
    .replace(/#/g, '%23')
    .replace(/{/g, '%7B')
    .replace(/}/g, '%7D')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E');

  // Return cursor CSS with hotspot at tip of pencil (position: 2, 2)
  return `url("data:image/svg+xml,${encodedSvg}") 2 2, crosshair`;
};
